import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import {
  getCategoryListingPaginate,
  getManufacturesListingPaginate,
} from '../../api/context/manufacturer';
import { getBrandListing } from '../../api/context/brand';

const CreateUpdateAutoNotificationModal = ({
  show,
  onHide,
  notification,
  onSave,
  onCancel,
  onFieldChange,
  saving = false
}) => {
  const [navigationType, setNavigationType] = useState('');
  const [navigationData, setNavigationData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const navigationTypes = [
    // { value: 'Homepage', label: 'Homepage' },
    { value: 'Brand', label: 'Brand' },
    { value: 'Vendor', label: 'Vendor' },
    { value: 'Store', label: 'Store' }, // 🆕 Added Store option
    // { value: 'Category', label: 'Category' },
  ];

  useEffect(() => {
    const navigateTo = notification?.navigateToPage;
    if (!navigateTo) {
      setNavigationType('');
      setSelectedData(null);
      setNavigationData([]);
      return;
    }

    const [type, name = ''] = navigateTo.split('/');
    if (type === navigationType) return;

    setNavigationType(type);
    if (type && type !== 'Homepage') {
      loadNavigationData(type, name);
    } else if (name) {
      setSelectedData({ value: name, label: name });
    } else {
      setSelectedData(null);
    }
  }, [notification?.navigateToPage, show]);

  // ✅ Load dropdown data based on type
  const loadNavigationData = async (type, existingValue = '') => {
    setLoadingData(true);
    setNavigationData([]);
    setSelectedData(null);

    try {
      const updateSelection = (list) => {
        setNavigationData(list);
        if (existingValue) {
          const found = list.find(item => item.value === existingValue);
          if (found) setSelectedData(found);
        }
        setLoadingData(false);
      };

      switch (type) {
        case 'Brand':
          getBrandListing({ page: 1, pageSize: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const brands = response?.data?.map(brand => ({
                value: brand.name,
                label: brand.name,
                id: brand.id,
              }));
              updateSelection(brands);
            } else setLoadingData(false);
          });
          break;

        case 'Vendor':
        case 'Store': // 🆕 Reuse same API logic for Store
          getManufacturesListingPaginate({ page: 1, limit: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const vendors = response?.results?.map(vendor => ({
                value: vendor.label,
                label: vendor.label,
                id: vendor.value,
              }));
              updateSelection(vendors);
            } else setLoadingData(false);
          });
          break;

        case 'Category':
          getCategoryListingPaginate({ page: 1, limit: 100, search: '' })((response) => {
            if (response?.statusCode === 200) {
              const categories = response?.results?.map(cat => ({
                value: cat.label,
                label: cat.label,
                id: cat.id,
              }));
              updateSelection(categories);
            } else setLoadingData(false);
          });
          break;

        default:
          setLoadingData(false);
          break;
      }
    } catch (error) {
      console.error('Error loading navigation data:', error);
      setLoadingData(false);
    }
  };

  // ✅ Handle navigation change
  const handleNavigationTypeChange = (selectedOption) => {
    const type = selectedOption?.value || '';
    setNavigationType(type);
    setSelectedData(null);

    if (onFieldChange) {
      onFieldChange('navigationType', type);
      onFieldChange('navigationId', null);
      onFieldChange('navigateToPage', type);
    }

    if (type && type !== 'Homepage') {
      loadNavigationData(type);
    }
  };

  // ✅ Handle target item change
  const handleDataChange = (selectedOption) => {
    setSelectedData(selectedOption);

    if (selectedOption && navigationType) {
      const navigateToPage = `${navigationType}/${selectedOption.value}`;
      if (onFieldChange) {
        onFieldChange('navigateToPage', navigateToPage);
        onFieldChange('navigationType', navigationType);
        onFieldChange('navigationId', selectedOption.id || null);
      }
    } else if (navigationType === 'Homepage') {
      if (onFieldChange) {
        onFieldChange('navigateToPage', 'Homepage');
        onFieldChange('navigationType', 'Homepage');
        onFieldChange('navigationId', null);
      }
    } else {
      if (onFieldChange) {
        onFieldChange('navigateToPage', '');
        onFieldChange('navigationId', null);
      }
    }
  };

  const getDataPlaceholder = () => {
    if (loadingData) return 'Loading...';
    switch (navigationType) {
      case 'Brand': return 'Select brand';
      case 'Vendor': return 'Select vendor';
      case 'Store': return 'Select store'; // 🆕 Added Store label
      case 'Category': return 'Select category';
      case 'Homepage': return 'No selection needed';
      default: return 'Select type first';
    }
  };

  const isDataSelectionRequired = () =>
    navigationType && navigationType !== 'Homepage';

  const isEditable = !notification?.id ||
    (notification?.status?.toLowerCase() === 'upcoming' ||
      notification?.status?.toLowerCase() === 'scheduled');

  const handleFieldChange = (field, value) => {
    if (onFieldChange) onFieldChange(field, value);
  };

  const getModalTitle = () =>
    !notification?.id ? 'Schedule New Notification' : 'Notification Details';

  const getSaveButtonText = () => {
    if (saving) return !notification?.id ? 'Scheduling...' : 'Updating...';
    return !notification?.id ? 'Schedule Notification' : 'Update Notification';
  };

  return (
    <Modal show={show} onHide={onHide} scrollable centered size="md">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="w-100">
          <div className="d-flex align-items-center gap-2 my-2">
            <h5 className="fw-bold mb-0">{getModalTitle()}</h5>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pt-2 pb-5">
        {notification && (
          <div className="notification-form">
            {/* Title */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={notification.title || ''}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="Enter notification title"
                disabled={saving || !isEditable}
              />
            </Form.Group>

            {/* Message */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">
                Message <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notification.message || ''}
                onChange={(e) => handleFieldChange('message', e.target.value)}
                placeholder="Enter notification message"
                disabled={saving || !isEditable}
              />
            </Form.Group>

            {/* Navigation & Target */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Navigation</Form.Label>
                  <Select
                    options={navigationTypes}
                    value={navigationTypes.find(opt => opt.value === navigationType)}
                    onChange={handleNavigationTypeChange}
                    placeholder="Select"
                    isDisabled={saving || !isEditable}
                    isLoading={loadingData}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Target</Form.Label>
                  <Select
                    options={navigationData}
                    value={selectedData}
                    onChange={handleDataChange}
                    placeholder={getDataPlaceholder()}
                    isDisabled={!isDataSelectionRequired() || saving || !isEditable}
                    isLoading={loadingData}
                    isSearchable
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Schedule Date/Time */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={notification.scheduledDate || ''}
                    onChange={(e) => handleFieldChange('scheduledDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={saving || !isEditable}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Time <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="time"
                    value={notification.scheduledTime || ''}
                    onChange={(e) => handleFieldChange('scheduledTime', e.target.value)}
                    disabled={saving || !isEditable}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <div className="d-flex justify-content-end w-100 gap-3">
          <Button className="cancel_btn" onClick={onCancel} disabled={saving}>
            {isEditable ? 'Cancel' : 'Close'}
          </Button>
          {isEditable && (
            <Button className="request_btn" onClick={onSave} disabled={saving}>
              {getSaveButtonText()}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateUpdateAutoNotificationModal;
